<script lang="ts">
  import { ParticipationOption } from "@models/attendance/ParticipationOption"
  import { adjustedBalance, Adjustment, newAdjustment, validateAdjustment } from "@models/payments/Adjustment"
  import { post, put } from "@util/api"
  import RadioButton from "@components/ui/RadioButton.svelte"
  import AmountInput from "./ui/AmountInput.svelte"
  import DangerousAction from "./ui/DangerousAction.svelte"
  import ErrorMessage from "./ui/ErrorMessage.svelte"
  import Fieldset from "./ui/Fieldset.svelte"
  import Form from "./ui/Form.svelte"
  import Label from "./ui/Label.svelte"
  import SubmitButton from "./ui/SubmitButton.svelte"
  import DeleteButton from "./ui/DeleteButton.svelte"
  import { formatCentsAsUsd } from "@util/money"
  import AdjustmentTypes from "./AdjustmentForm/AdjustmentTypes.svelte"

  interface BalanceOption {
    id: number
    amount: number
  }

  export let tripId: number
  export let userId: number
  export let tripHasFinished: boolean
  export let deletePath: string | undefined
  export let existingAdjustmentInsuranceDiff: number = 0
  export let insuranceDiffPath: string
  export let path: string
  export let participationOptions: ParticipationOption[]
  export let balances: BalanceOption[]
  export let adjustment: Adjustment = newAdjustment()

  let initialAdjustment = (adjustment.price || 0) + existingAdjustmentInsuranceDiff
  let initialBalance
  let currentBalance
  let selectedParticipation: ParticipationOption
  let newAdjustmentCreation = adjustment.id === undefined
  let disableAdjustmentTypes: boolean = false
  let insuranceSelectionAllowed = !tripHasFinished

  $: {
    if (adjustment.price) {
      adjustment.price = Math.abs(adjustment.price)
    }

    if (adjustment.participationId) {
      let balance = balances.find((a) => a.id === adjustment.participationId)
      selectedParticipation = participationOptions.find((opt) => opt.id === adjustment.participationId)

      insuranceSelectionAllowed = !tripHasFinished && !selectedParticipation.cancelled
      disableAdjustmentTypes = !newAdjustmentCreation && !insuranceSelectionAllowed

      if (insuranceSelectionAllowed && adjustment.transactionType === "credit") {
        adjustment.coveredByInsurance = true
      }

      if (!insuranceSelectionAllowed && newAdjustmentCreation) {
        adjustment.coveredByInsurance = false
      }

      // If we're editing this adjustment, display the account balance without this adjustment applied
      initialBalance = balance.amount - initialAdjustment
      currentBalance = adjustedBalance(adjustment, insuranceDiffPath, userId, initialBalance)
    } else {
      initialBalance = null
      currentBalance = null
    }
  }

  function submit() {
    if (adjustment.id) {
      return put(path, { adjustment, tripId, userId })
    } else {
      return post(path, { adjustment, tripId, userId })
    }
  }

  function resetCoveredByInsurance() {
    adjustment.coveredByInsurance = undefined
  }
</script>

<Form data={adjustment} validate={validateAdjustment} {submit} let:submitted let:submitting let:errors>
  <Fieldset legend="Adjustment Options">
    <div slot="description">
      <p>Select a passenger and describe why you&apos;re adjusting their balance.</p>
    </div>
    <div>
      <Label name="participationId">Passenger</Label>
      <select
        id="participationId"
        name="participationId"
        bind:value={adjustment.participationId}
        on:change={resetCoveredByInsurance}
        disabled={adjustment.id !== undefined}>
        <option value={undefined} disabled>Choose a Passenger</option>
        {#each participationOptions as option}
          <option value={option.id}>{option.passengerName}</option>
        {/each}
      </select>
      <ErrorMessage when={submitted} error={errors.participationId} />
    </div>

    <div>
      <Label name="description">Description</Label>
      <input
        name="description"
        id="description"
        data-role="description-input"
        type="text"
        bind:value={adjustment.description}
        placeholder="Describe what this adjustment represents" />
      <ErrorMessage when={submitted} error={errors.description} />
    </div>
  </Fieldset>

  <Fieldset legend="Adjustment Type">
    <div slot="description">
      <p>Choose among different types of adjustments</p>
    </div>
    <div>
      <DangerousAction>
        <h2 class="text-gray-900 text-lg mb-2">Do not use balance adjustments to transfer money between passengers</h2>
        <p class="max-w-para whitespace-normal">
          You should issue a manual refund and a manual charge instead. For more information
          <a
            href="https://groupcollect.helpscoutdocs.com/article/87-how-do-i-move-funds-from-one-passenger-account-to-another"
            target="_blank"
            class="underline">see this help document</a
          >.
        </p>
      </DangerousAction>
    </div>
    <div>
      <Label name="transactionType">Adjustment Type</Label>

      <AdjustmentTypes bind:adjustment {resetCoveredByInsurance} disabled={disableAdjustmentTypes} />

      <ErrorMessage when={submitted} error={errors.transactionType} />
    </div>
  </Fieldset>

  {#if insuranceSelectionAllowed && adjustment.transactionType !== "credit"}
    <Fieldset legend="Travel Protection">
      <div slot="description">
        <p>Choose whether the adjustment is covered by travel protection or not</p>
      </div>

      <div>
        <RadioButton name="coveredByInsurance" bind:group={adjustment.coveredByInsurance} value={true}>
          <span class="font-semibold">Сovered by travel protection</span>
        </RadioButton>
        <ul class="ml-7 mb-3">
          <li>
            <span class="italic text-gray-700">Note: </span> This will affect passenger premium and balance.
          </li>
          <li>
            <span class="italic text-gray-700">Example: </span> Additional travel expensive, air fare, touring costs, etc.
          </li>
        </ul>

        <RadioButton name="coveredByInsurance" bind:group={adjustment.coveredByInsurance} value={false}>
          <span class="font-semibold">Not сovered by travel protection</span>
        </RadioButton>
        <ul class="ml-7 mb-3">
          <li>
            <span class="italic text-gray-700">Note: </span> This will <strong>not</strong> affect passenger premium and balance.
          </li>
          <li>
            <span class="italic text-gray-700">Example: </span> Clothing, backpacks, non-travel related expenses.
          </li>
        </ul>

        <ErrorMessage when={submitted} error={errors.coveredByInsurance} />
      </div>
    </Fieldset>
  {/if}

  <Fieldset legend="Amount and Preview">
    <div slot="description">
      <p>Enter an adjustment amount and see a preview of how your adjustment changes the passenger's due balance</p>
    </div>
    <div>
      <div>
        <Label name="amount">Amount</Label>
        <AmountInput name="amount" dataRole="price-input" bind:value={adjustment.price} />
        <ErrorMessage when={submitted} error={errors.price} />
      </div>

      <div class="grid grid-flow-col justify-start gap-inline-12 items-center mt-4">
        <div class="text-base" data-role="current-account-balance" data-testid="current-account-balance">
          Due Before Adjustment
          <div class="text-lg font-bold">
            {formatCentsAsUsd(initialBalance)}
          </div>
        </div>
        <div class="balance-preview-arrow-icon w-10 -mt-4" />
        <div class="text-base" data-role="preview-new-account-balance" data-testid="preview-new-account-balance">
          Due After Adjustment
          <div class="text-lg font-bold">
            {#await currentBalance then currentBalance}
              {formatCentsAsUsd(currentBalance)}
            {/await}
          </div>
        </div>
      </div>
    </div>
  </Fieldset>

  <Fieldset legend="Issue Adjustment">
    <div slot="description">
      <p>
        <strong>Note: </strong>If you&apos;re editing a trip cost across multiple passengers, you should edit the trip
        package price instead.
      </p>
    </div>

    <div class="button-row">
      <SubmitButton {submitting}>
        {#if adjustment.id}
          Save Adjustment
        {:else}
          Issue Adjustment
        {/if}
      </SubmitButton>

      {#if deletePath}
        <DeleteButton path={deletePath} confirmMsg="Are you sure you want to delete this adjustment?">
          Delete Adjustment
        </DeleteButton>
      {/if}
    </div>
  </Fieldset>
</Form>
